import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getOrders } from '@/lib/api';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';
import { useAuth } from '@/lib/AuthContext';
import Navbar from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, ArrowRight, Truck, CheckCircle, Clock, Box, Shield, MapPin, Hash } from 'lucide-react';

const STATUS_CONFIG = {
  pending_payment: { label: 'Awaiting Payment', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  processing: { label: 'Processing', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  packed: { label: 'Packed', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Box },
  shipped: { label: 'Shipped', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
};

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getOrders();
        setOrders(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
    else setLoading(false);
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
          <Package className="w-16 h-16 text-primary mx-auto mb-6 opacity-40" />
          <h2 className="font-display text-3xl font-bold mb-4">Sign in to view orders</h2>
          <Link to="/login"><Button className="bg-primary text-primary-foreground font-bold uppercase rounded-sm h-12 px-8" data-testid="orders-login-btn">Sign In</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="orders-page">
      <Navbar />
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">My Orders</h1>
          </div>
          <p className="text-muted-foreground mb-8">Track your purchases and shipments</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order, i) => {
              const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG.processing;
              const StatusIcon = statusConf.icon;
              return (
                <motion.div
                  key={order.order_id}
                  className="bg-card border border-border/40 hover:border-primary/30 rounded-sm p-5 transition-colors duration-300"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <img src={order.image_url} alt={order.collectible_name} className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-muted-foreground">{order.order_id}</span>
                        <Badge variant="outline" className={`text-xs rounded-sm ${statusConf.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" /> {statusConf.label}
                        </Badge>
                      </div>
                      <h3 className="font-display font-bold truncate">{order.collectible_name}</h3>
                      <p className="text-xs text-muted-foreground">Serial: {order.serial_number}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`${MONEY_CLASSNAME} text-sm`}>{formatInr(order.purchase_price)}</p>
                      <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      <Link to={`/certificate/${order.certificate_id}`} className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors mt-2" data-testid={`view-cert-${order.order_id}`}>
                        <Shield className="w-3 h-3" /> View Certificate
                      </Link>
                    </div>
                  </div>

                  {/* Shipping Details */}
                  {(order.tracking_number || order.carrier || order.estimated_delivery) && (
                    <div className="mt-3 pt-3 border-t border-border/30 flex flex-wrap gap-x-5 gap-y-1.5" data-testid={`tracking-info-${order.order_id}`}>
                      {order.carrier && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Truck className="w-3 h-3" /> {order.carrier}
                        </span>
                      )}
                      {order.tracking_number && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 font-mono">
                          <Hash className="w-3 h-3" /> {order.tracking_number}
                        </span>
                      )}
                      {order.estimated_delivery && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Est. delivery: {new Date(order.estimated_delivery).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Status Timeline */}
                  {order.status_history && (
                    <div className="mt-4 pt-4 border-t border-border/40">
                      <div className="flex items-center gap-1 overflow-x-auto">
                        {['processing', 'packed', 'shipped', 'out_for_delivery', 'delivered'].map((step, si) => {
                          const isCompleted = order.status_history.some(h => h.status === step);
                          const isCurrent = order.status === step;
                          return (
                            <div key={step} className="flex items-center flex-shrink-0">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                isCompleted ? 'bg-primary text-primary-foreground' : isCurrent ? 'bg-primary/30 text-primary border border-primary' : 'bg-muted/50 text-muted-foreground'
                              }`}>
                                {isCompleted ? <CheckCircle className="w-3 h-3" /> : si + 1}
                              </div>
                              {si < 4 && <div className={`w-8 h-0.5 ${isCompleted ? 'bg-primary' : 'bg-muted/50'}`} />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="font-display text-2xl font-bold mb-2">No Orders Yet</h3>
            <p className="text-muted-foreground mb-6">Purchase a collectible to see your orders here.</p>
            <Link to="/"><Button className="bg-primary text-primary-foreground font-bold uppercase rounded-sm h-12 px-8" data-testid="browse-btn">Browse Drops <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
